/**
 * This is the starting point of your application.
 * Feature Hub Apps will use this file to bootstrap the app.
 */
import React, { useEffect } from 'react';
import { GfaServiceConfigProviderV1 } from '@volkswagen-onehub/gfa-service-config-provider';
import { createTrackingService } from '@volkswagen-onehub/oneaudi-os-adapter-tracking';
import { ThemeProvider } from 'styled-components';
import { ThemeProvider as ThemeProviderAlpha } from '@oneaudi/unified-web-common';
import { ApolloProvider } from '@apollo/client';
import { useDispatch } from 'react-redux';
import GlobalStyles from './styles';
import { COLORS } from './styles/variables';
import { apolloClient, IS_LEGACY, IS_LOCAL_ENVIRONMENT } from './constants/environment';
import ErrorBoundary from './components/ErrorBoundary';
import DigitalLeads from './components/DigitalLeads';
import usePrepareConfig from './hooks/usePrepareConfig';
import { setAppConfig } from './redux/appConfigReducer';
import { ConfigTypes } from './types';
import { setUserInputs } from './redux/inputsReducer';
import LocalCtas from './components/LocalCtas';
import useLoadAppConfig from './hooks/useLoadAppConfig';

declare global {
  interface Window {
    ss: {
      visitor: {
        getMarketingCloudVisitorID(): string;
      };
    };
    adobeDataLayer: [
      {
        userData: {
          userECID: string;
        };
      },
    ];
  }
}
interface FeatureAppProps {
  configProvider?: GfaServiceConfigProviderV1;
  trackingService?: any;
  remoteConfig: ConfigTypes;
}

const FeatureApp: React.FC<FeatureAppProps> = ({
  // @ts-ignore
  trackingService = typeof window === 'undefined'
    ? undefined
    : createTrackingService(__FEATURE_APP_NAME__),
  remoteConfig,
}: FeatureAppProps) => {
  const dispatch = useDispatch();
  useEffect(() => {
    if (!trackingService) {
      return;
    }

    // eslint-disable-next-line no-param-reassign
    trackingService.featureAppName = __FEATURE_APP_NAME__;

    const readyEvent = {
      event: {
        attributes: {
          componentName: __FEATURE_APP_NAME__,
          elementName: '',
          label: '',
          clickID: '',
          pos: '',
          targetURL: '',
          value: '',
        },
        eventInfo: {
          eventAction: 'feature_app_ready',
          eventName: `${__FEATURE_APP_NAME__} - Feature App ready`,
        },
      },
    };
    trackingService.track(readyEvent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const testRemoteConfig4 = {
    market: 'USA',
    dealerId: null,
    vehicleId: null,
    vehicleType: null,
    modelSalesCode: '',
    presetForm: 'contact-dealer',
    customForm: ['topicOfInterest', 'firstName', 'lastName', 'preferredContactMethod'],
    departmentName: 'Test dep name',
    phoneNumberCMS: 4385301015,
    tcpaConsent: '<p> TCPA consent</p>',
    headingCustomText: null,
    bodyCustomText: null,
    ctaVerbiageCustom: null,
    ctaUrlCustom: null,
    topicOfInterestList: [],
    ccpaDisclaimer: null,
    handraiser: null,
  };

  const preparedConfig = usePrepareConfig(IS_LOCAL_ENVIRONMENT ? testRemoteConfig4 : remoteConfig);

  // Modifying regular config with HR forms if they exists
  const { getAppConfig } = useLoadAppConfig();

  useEffect(() => {
    const configPayload = getAppConfig(preparedConfig);

    dispatch(setAppConfig(configPayload));
  }, [dispatch, preparedConfig, getAppConfig]);

  // When we have pre-set Zip code (for Offers page), we pre-set it into the input reducer

  useEffect(() => {
    if (!preparedConfig.isOfferPage && !preparedConfig.offers?.zip) return;
    dispatch(setUserInputs({ inputName: 'zipCode', inputValue: preparedConfig.offers?.zip }));
  }, [dispatch, preparedConfig.isOfferPage, preparedConfig.offers?.zip]);

  const appContent = (
    <ApolloProvider client={apolloClient}>
      <div className="resetWrapper">
        {IS_LOCAL_ENVIRONMENT && <LocalCtas />}
        <DigitalLeads />
      </div>
    </ApolloProvider>
  );

  return (
    <div>
      {/* @ts-ignore:next-line */}
      <ErrorBoundary>
        {IS_LEGACY ? (
          <ThemeProvider theme={COLORS}>
            {appContent}
            <GlobalStyles />
          </ThemeProvider>
        ) : (
          <ThemeProviderAlpha>{appContent}</ThemeProviderAlpha>
        )}
      </ErrorBoundary>
    </div>
  );
};

export default FeatureApp;
